import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { UserService } from '../../services/user.service';
import { WorkspaceService } from '../../services/workspace.service';
import { FormsModule } from '@angular/forms';
import { UserStatusComponent } from '../../components/user/user-status/user-status.component';
import { BillingStatus, UserInterface, WorkspaceInterface, TransferActivity } from 'voxr-shared';
import { WorkspaceRouterLinkDirective } from '../../directives/wRouterLink.directive';
import { AppService } from '../../services/app.service';
import { firstValueFrom, merge } from 'rxjs';
import { MatDrawerMode, MatSidenavModule } from "@angular/material/sidenav";
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ConnectionStatsComponent } from '../../components/general/connection-stats/connection-stats.component';
import { TransferCoachComponent } from '../../components/workspace/live-transfers/transfer-coach/transfer-coach.component';
import { ActivityService } from '../../services/activity.service';
import { CampaignService } from '../../services/campaign.service';

export const ADMIN_SYSTEM_LINKS = [
  { path: '/admin/transfer-presets', icon: 'pi-share-alt', label: 'Transfer Presets' },
  { path: '/admin/experiments', icon: 'pi-flask', label: 'Experiments' },
  { path: '/admin/api-credentials', icon: 'pi-key', label: 'API Credentials' },
  { path: '/admin/logs', icon: 'pi-chart-line', label: 'System Logs' },
] as const;

@Component({
  selector: 'app-sidebar-layout',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterOutlet, RouterLink, RouterLinkActive,
    MatSidenavModule, MatIconModule, MatButtonModule, MatTooltipModule, MatFormFieldModule, MatSelectModule,
    UserStatusComponent, WorkspaceRouterLinkDirective, ConnectionStatsComponent, TransferCoachComponent
  ],
  templateUrl: './sidebar-layout.component.html',
  styleUrl: './sidebar-layout.component.scss'
})
export class SidebarLayoutComponent {
  readonly adminSystemLinks = ADMIN_SYSTEM_LINKS;
  private app = inject(AppService);
  private userService = inject(UserService);
  private workspaceService = inject(WorkspaceService);
  private activityService = inject(ActivityService);
  private campaignService = inject(CampaignService);
  private route = inject(ActivatedRoute);

  canceledSubscriptionStatuses: BillingStatus[] = ['canceled', 'incomplete_expired', 'reseller-inactive', 'negative-balance'];
  showSidebar = true;
  user?: UserInterface | null;
  workspaces?: WorkspaceInterface[];
  workspace?: WorkspaceInterface | null;
  coachOnly = false;
  area?: string;
  needOnboarding?: boolean;
  activeCoachTransfer = signal<TransferActivity | undefined>(undefined);
  canViewLiveTransfers = signal(false);
  brandName = this.app.brandName;
  logoUrl = this.app.brandLogo;
  sidenav = true;
  sidenavMode: MatDrawerMode = 'side';
  private loadGeneration = 0;

  constructor() {
    merge(this.userService.user$, this.workspaceService.workspaces$, this.workspaceService.workspace$)
      .pipe(takeUntilDestroyed()).subscribe(async () => await this.load());

    this.activityService.activeCoachTransfer$.pipe(takeUntilDestroyed()).subscribe(transfer => {
      this.activeCoachTransfer.set(transfer);
    });
  }

  async ngOnInit() {
    this.area = this.route.snapshot.data['area'];
    const desktop = await firstValueFrom(this.app.isDesktop$);
    this.sidenav = desktop;
    this.sidenavMode = desktop ? 'side' : 'over';
  }

  externalResellerDomain?: string;
  private async load() {
    const generation = ++this.loadGeneration;
    const user = await firstValueFrom(this.userService.user$);
    if (generation !== this.loadGeneration) return;
    const userId = user?._id?.toString();
    if (!userId || userId !== this.user?._id?.toString()) {
      this.workspaces = undefined;
      this.workspace = undefined;
      this.coachOnly = false;
      this.canViewLiveTransfers.set(false);
    } else if (this.workspace?._id?.toString() !== this.workspaceService.workspaceId) {
      this.workspace = undefined;
      this.coachOnly = false;
      this.canViewLiveTransfers.set(false);
    }
    this.user = user;
    this.externalResellerDomain = user?.resellerConfig?.whiteLabel?.domains?.length
      && !user.resellerConfig.whiteLabel.domains.includes(location.origin)
      ? user.resellerConfig.whiteLabel.domains[0] : undefined;
    if (!userId) return;

    const workspaces = await firstValueFrom(this.workspaceService.workspaces$);
    const workspace = await firstValueFrom(this.workspaceService.workspace$);
    const workspaceId = workspace?._id?.toString();
    if (generation !== this.loadGeneration || this.workspaceService.workspaceId !== workspaceId) return;
    const coachOnly = !!workspace?._metadata?.coachOnly;
    const coachAllowed = !!workspaceId && (coachOnly || !!workspace?.members?.some(item => item.accepted
      && item.userId?.toString() === userId && item.roles.includes('coach')));
    if (workspaces !== undefined)
      this.workspaces = workspaces.filter(_ => _.ownerId == userId || _.members?.find(m => m.userId == userId));
    this.workspace = workspace;
    this.coachOnly = coachOnly;
    this.canViewLiveTransfers.set(coachAllowed);
    let independentlyAllowed = false;
    if (!coachAllowed && userId && workspaceId) {
      try {
        independentlyAllowed = await this.workspaceService.hasAdminAccess();
        if (generation !== this.loadGeneration) return;
        if (!independentlyAllowed)
          independentlyAllowed = await this.campaignService.currentUserCanSuperviseWorkspace(workspaceId);
      } catch { /* A failed independent lookup must not hide an additional Coach grant. */ }
    }
    if (generation !== this.loadGeneration || this.workspaceService.workspaceId !== workspaceId) return;
    this.canViewLiveTransfers.set(coachAllowed || independentlyAllowed);
  }

  async setWorkspace(workspaceId: string) {
    this.workspaceService.setWorkspace(workspaceId);
    // this.workspaceService.workspace$
    //   .pipe(filter(w => w?._id?.toString() == workspaceId)).subscribe(() => window.location.href = '/dashboard');
  }

  onCoachLeft() {
    this.activityService.setActiveCoachTransfer(undefined);
  }

}

